// The Shipping hub's order-row actions, ONE copy for the desktop hub (./ShippingHub.jsx) and the phone Shipping
// screen (mobile/screens/ShippingScreen.jsx), 2026-10-29. Each takes the screen's `ui` (the Working-as ui over
// desktopUi or phoneUi) and its busy setter, and resolves to the line the screen shows ({text, ok}) or null when the
// person backed out; `reload` re-reads the board when something changed. The screen keeps its own one-at-a-time
// guard and clears its message before calling.
import { confirmPickupAll } from '../salesOrders/palletActions';
import { operatorPinUi } from '../salesOrders/pickupQueue';
import { SIGNING_OFF } from './signingFlags';
import { startSignedPickup } from './pickupSigning';
import { needsDeliveryCode, startDeliveryQr } from './deliverySigning';
import { dueText, lanePallets, pickupPlan, primaryAction } from './soBoard';
import { absoluteUrl, loadManifestPath, readSignRequest, reissueSignRequest } from '../signing/signRequestsApi';
import { loadVehicleText } from '../signing/signWords';

const noop = () => {};

async function openSignPanel(props) {
  const { signRequestPrompt } = await import('../signing/SignRequestPanel');
  return signRequestPrompt(props);
}

/**
 * What one order row shows in a lane: due words, its ONE action ({kind, label}: soBoard.primaryAction), the delivery
 * extras of Out for delivery (outInfo: load / signature shown; qrPending: a live code to show again; needCode: no live
 * code, start one) and the pallet its package guide opens on.
 */
export function laneRowView(order, lane, signing = SIGNING_OFF) {
  const outInfo = !!(lane === 'out' && signing.delivery && (order.load || order.sign || order.delivered_at));
  const qrPending = !!(outInfo && order.sign && order.sign.status === 'pending' && order.sign.request_id != null);
  const needCode = lane === 'out' && !qrPending && needsDeliveryCode(order, signing);
  const first = lanePallets(order, lane)[0];
  return {
    due: dueText(order.promised_date),
    act: primaryAction(order, lane, signing),
    outInfo,
    qrPending,
    needCode,
    firstPalletId: first ? first.pallet_id : null,
  };
}

/** Customer is here: start pickup. Signed by the customer (shipping_signing.pickup), else the plain Confirm pickup all. */
export async function startPickup({ apiBase, order, ui, signing = SIGNING_OFF, setBusy = noop, reload = noop }) {
  if (signing.pickup) {
    const r = await startSignedPickup({
      apiBase,
      plan: { ...pickupPlan(order), customer_name: order.customer_name },
      ui,
      setBusy,
    });
    if (r && r.outcome) reload();
    return r && r.message ? { text: r.message, ok: !!r.done } : null;
  }
  const r = await confirmPickupAll({
    plan: pickupPlan(order),
    stagedCount: lanePallets(order, 'pickup').length || Number(order.counts.staged || 0),
    ui: operatorPinUi(ui, order),
    setBusy,
    reloadPlan: reload,
  });
  return r && r.message ? { text: `${order.order_number}: ${r.message}`, ok: !!r.done } : null;
}

/** Load truck closed with a load: the line the hub shows. */
export function loadedMessage(order) {
  return { text: `${order ? order.order_number : 'The order'}: loaded. It is in Out for delivery.`, ok: true };
}

// Out for delivery: the delivery QR again (the same code as on the manifest). GET answers it when the API keeps it;
// otherwise a reissue (signed by the Working-as session) hands back the same code.
export async function showSigningQr({ apiBase, order, ui, setBusy = noop, reload = noop }) {
  const sign = order.sign;
  if (!sign || sign.request_id == null) return null;
  setBusy(true);
  let req = null;
  const r = await readSignRequest(apiBase, sign.request_id);
  if (r.ok && r.body && r.body.qr_svg) req = { ...r.body, request_id: sign.request_id };
  if (!req) {
    setBusy(false);
    const pin = await ui.identity('load');
    if (!pin) return null;
    setBusy(true);
    const again = await reissueSignRequest(apiBase, sign.request_id, { pin });
    if (!again.ok || !again.body) {
      setBusy(false);
      return { text: `${order.order_number}: couldn't show the signing QR: ${again.message}`, ok: false };
    }
    req = again.body;
  }
  setBusy(false);
  const outcome = await openSignPanel({
    apiBase, ui, request: req, kind: 'delivery', orderNumber: order.order_number,
    title: `Delivery · ${order.customer_name || order.order_number}`,
    subtitle: `${order.order_number}${loadVehicleText(order.load) ? ` · ${loadVehicleText(order.load)}` : ''}`,
    canNewCode: false, canCancel: false,
  });
  if (outcome && outcome.status === 'signed') {
    reload();
    return { text: `${order.order_number}: delivered · signed${outcome.signed && outcome.signed.name ? ` by ${outcome.signed.name}` : ''}.`, ok: true };
  }
  return null;
}

// Out for delivery with no live code (shipped the plain way, or the code expired / was cancelled): start one (the
// pending one again when there is one) and show it. jobq-api POST /packaging/plans/{id}/delivery_signing.
export async function startDeliveryCode({ apiBase, order, ui, setBusy = noop, reload = noop }) {
  const r = await startDeliveryQr({
    apiBase, ui, planId: order.plan_id, orderNumber: order.order_number, customerName: order.customer_name, setBusy,
  });
  if (r.outcome) reload();
  return r.message ? { text: r.message, ok: !!r.done } : null;
}

/** The load's manifest (the signing QR is on it) as a document {url, filename, what}; null without a load. */
export function loadManifestDocument(apiBase, order) {
  const id = order && order.load && order.load.load_id;
  if (id == null) return null;
  return {
    url: absoluteUrl(apiBase, order.load.manifest_url || loadManifestPath(id)),
    filename: `Manifest_${order.order_number}.pdf`,
    what: `${order.order_number} manifest`,
  };
}

/** Desktop: open the load's manifest (ui.openPdfTab). Resolves to an error line, or null. */
export async function openLoadManifest({ apiBase, order, ui }) {
  const doc = loadManifestDocument(apiBase, order);
  if (!doc) return null;
  try {
    await ui.openPdfTab(doc.url, doc.filename);
    return null;
  } catch (e) {
    return { text: `${order.order_number}: couldn't open the manifest: ${e.message || e}`, ok: false };
  }
}
